import { useEffect, useSyncExternalStore } from 'react';
import { getServerState } from '@api/jobqueues';
import type { AnalysisType } from './analysisConfig';
import { FORCE_ENABLE_ALL_SERVER_FEATURES_FOR_DEV } from './config';

export type ServerFeatureLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ServerFeatureSnapshot {
  status: ServerFeatureLoadStatus;
  names: string[];
  normalizedNames: string[];
  accessToken?: string;
  error?: unknown;
}

type FeatureRequirement =
  | { mode: 'always' }
  | { mode: 'any'; names: string[] }
  | { mode: 'all'; names: string[] };


const DEV_ALL_SERVER_FEATURE_NAMES = [
  'default',
  'segmentation',
  'coronary',
  'aorta',
  'aortacenterline',
  'lawt',
  'ct-ffr',
  'data-generator',
  'onnxruntime',
  'autotavi',
];

const normalizeServerFeatureName = (name: unknown) =>
  String(name ?? '')
    .trim()
    .toLowerCase();

const createSnapshot = (
  status: ServerFeatureLoadStatus,
  names: string[] = [],
  accessToken?: string,
  error?: unknown,
): ServerFeatureSnapshot => {
  const normalizedNames = Array.from(
    new Set(names.map(normalizeServerFeatureName).filter(Boolean)),
  );

  return {
    status,
    names,
    normalizedNames,
    accessToken,
    error,
  };
};

let snapshot: ServerFeatureSnapshot = createSnapshot('idle');
let pendingPromise: Promise<string[]> | null = null;
let pendingAccessToken: string | undefined;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

export const getServerFeatureSnapshot = () => snapshot;

export const subscribeServerFeatureState = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const toServerFeatureNames = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item == null ? '' : String(item).trim()))
    .filter(Boolean);
};

export function ensureServerFeatureStateLoaded(accessToken?: string) {
  if (FORCE_ENABLE_ALL_SERVER_FEATURES_FOR_DEV) {
    if (snapshot.status !== 'ready' || snapshot.accessToken !== accessToken) {
      snapshot = createSnapshot('ready', DEV_ALL_SERVER_FEATURE_NAMES, accessToken);
      emit();
    }
    return Promise.resolve(snapshot.names);
  }

  if (!accessToken) return Promise.resolve(snapshot.names);

  if (
    snapshot.accessToken === accessToken &&
    (snapshot.status === 'ready' || snapshot.status === 'error')
  ) {
    return Promise.resolve(snapshot.names);
  }

  if (pendingPromise && pendingAccessToken === accessToken) {
    return pendingPromise;
  }

  pendingAccessToken = accessToken;
  snapshot = createSnapshot('loading', snapshot.names, accessToken);
  emit();

  pendingPromise = getServerState({ accessToken })
    .then((names) => {
      const nextNames = toServerFeatureNames(names);
      snapshot = createSnapshot('ready', nextNames, accessToken);
      emit();
      return nextNames;
    })
    .catch((error) => {
      console.error('Failed to load server feature state', error);
      snapshot = createSnapshot('error', [], accessToken, error);
      emit();
      return [];
    })
    .finally(() => {
      pendingPromise = null;
      pendingAccessToken = undefined;
    });

  return pendingPromise;
}

export function useServerFeatureState(accessToken?: string) {
  const state = useSyncExternalStore(
    subscribeServerFeatureState,
    getServerFeatureSnapshot,
    getServerFeatureSnapshot,
  );

  useEffect(() => {
    if (!accessToken) return;
    ensureServerFeatureStateLoaded(accessToken);
  }, [accessToken]);

  return state;
}

function hasAnyServerFeature(
  state: ServerFeatureSnapshot | undefined,
  names: readonly string[],
) {
  if (FORCE_ENABLE_ALL_SERVER_FEATURES_FOR_DEV) return true;
  if (!state || state.status !== 'ready') return false;

  const featureSet = new Set(state.normalizedNames);
  return names.some((name) => featureSet.has(normalizeServerFeatureName(name)));
}

function hasAllServerFeatures(
  state: ServerFeatureSnapshot | undefined,
  names: readonly string[],
) {
  if (FORCE_ENABLE_ALL_SERVER_FEATURES_FOR_DEV) return true;
  if (!state || state.status !== 'ready') return false;

  const featureSet = new Set(state.normalizedNames);
  return names.every((name) => featureSet.has(normalizeServerFeatureName(name)));
}

export function canUseCtFfrServerFeature(
  state: ServerFeatureSnapshot | undefined,
) {
  // ct-fft is accepted as a defensive alias for old typo/config values.
  return hasAnyServerFeature(state, ['ct-ffr', 'ct-fft']);
}

export function canUseDataGeneratorServerFeature(
  state: ServerFeatureSnapshot | undefined,
) {
  return hasAnyServerFeature(state, ['data-generator']);
}

export function getAnalysisTypeServerFeatureRequirement(
  analysisType: AnalysisType | string,
): FeatureRequirement {
  switch (analysisType) {
    case 'empty':
    case 'preview':
    case 'MPR':
      return { mode: 'always' };

    case 'segmentation':
      // Existing status logic treats either segmentation or coronary as a usable
      // segmentation-related server result, so menu visibility follows that rule.
      return { mode: 'any', names: ['segmentation', 'coronary'] };

    case 'plaque':
    case 'fai':
    case 'stenosis':
    case 'isr':
    case 'calciumScore':
      return { mode: 'any', names: ['coronary'] };

    case 'chamber':
      return { mode: 'any', names: ['lawt'] };

    case 'aorticValveCalcium':
      return { mode: 'all', names: ['aorta', 'aortacenterline'] };

    case 'tavi':
      return { mode: 'any', names: ['autotavi'] };

    case 'myocardial':
      return { mode: 'all', names: ['lawt', 'coronary'] };

    case 'wallthickness':
      return { mode: 'all', names: ['lawt'] };

    case 'ejectionFraction':
      return { mode: 'all', names: ['lawt'] };

    case 'FFR':
      return { mode: 'any', names: ['ct-ffr'] };

    default:
      return { mode: 'any', names: [String(analysisType).toLowerCase()] };
  }
}

export function isAnalysisTypeAvailableByServerState(
  analysisType: AnalysisType | string,
  state: ServerFeatureSnapshot | undefined,
) {
  if (FORCE_ENABLE_ALL_SERVER_FEATURES_FOR_DEV) return true;

  const requirement = getAnalysisTypeServerFeatureRequirement(analysisType);

  if (requirement.mode === 'always') return true;
  if (requirement.mode === 'all') {
    return hasAllServerFeatures(state, requirement.names);
  }
  return hasAnyServerFeature(state, requirement.names);
}
